import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ThreeBounce } from 'better-react-spinkit';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Button,
    Spinner
} from 'reactstrap';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import { doGET } from '../../util/HttpUtil';
import { buildPaymentReturnPath, paymentCopy } from './CartService';
import './stripe.css';
import PaymentStatusCard from './PaymentStatusCard';
import { FaSpinner } from 'react-icons/fa';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../InputField/testId';

const EmbeddedCheckoutUI = ({  unpaidCartId, onBackClick, onPaymentComplete, onError, testId }) => {
    const [stripeKey, setStripeKey] = useState('');
    const { showMessage } = useContext(DialogContext);
    const [clientSecret, setClientSecret] = useState('');
    const [processingPayment, setProcessingPayment] = useState(false)
    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(false);
    const pollingRef = useRef();
    const isMounted = useRef(true);
    const transactionIdRef = useRef(); // Add ref for transaction ID
    const { t } = useTranslation();
    const resolvedTestId = resolveAutomationTestId(testId);

    const stripePromise = useMemo(
        () => (stripeKey ? loadStripe(stripeKey) : null),
        [stripeKey]
    );

    useEffect(() => {
        transactionIdRef.current = transaction?._id;
    }, [transaction]);

    const handlePayNow = useCallback(async () => {
        if (!unpaidCartId) return;
        setLoading(true);
        try {
            const res = await doGET(`/api/transaction/cart/pay?cart_id=${unpaidCartId}&returnPath=${encodeURIComponent(buildPaymentReturnPath())}`);
            if (!isMounted.current) return;
            setClientSecret(res.data.stripeClientSecret);
            setTransaction(res.data);
        } catch (err) {
            console.error('Start Payment Error', err);
            if (isMounted.current) onError()
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [unpaidCartId]);

    const pollStatus = useCallback(async () => {
        const id = transactionIdRef.current; // Use ref instead of state
        if (!id) return;
        try {
            const res = await doGET(`/api/transaction/update-status?id=${id}`);
            if (!isMounted.current) return;
            if (res.data.stripeSessionStatus === 'complete') {
                clearInterval(pollingRef.current);
                onPaymentComplete();
            }
        } catch (err) {
            console.error('Polling Error', err);
        }
    }, [transactionIdRef?.current, transaction]);

    const onCheckoutComplete = useCallback(() => {
        showMessage('Payment captured');
        setProcessingPayment(true);
        pollingRef.current = setInterval(pollStatus, 5000);
    }, [pollStatus, showMessage]);

    const fetchStripeKey = useCallback(async () => {
        try {
            const res = await doGET('/api/sys-config/stripePublishableKey/detail');
            if (isMounted.current) setStripeKey(res.data.stripePublishableKey);
        } catch (err) {
            console.error('Stripe Key Error', err);
        }
    }, []);

    useEffect(() => {
        handlePayNow();
        fetchStripeKey();
        return () => {
            isMounted.current = false;
            clearInterval(pollingRef.current);
        };
    }, [unpaidCartId]);

    if (processingPayment) {
        return <PaymentStatusCard variant="processing" testId={buildAutomationChildTestId(resolvedTestId, "processing")} />;
    }

    if (loading) {
        return <div className="fv_pay_now_loading" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
            <FaSpinner className="fa-spin" />
            <p>{t(paymentCopy.loading)}</p>
            <div className="fv_amount_due_full_screen_loader" data-testid={buildAutomationChildTestId(resolvedTestId, "loading-overlay")}>
                <Spinner color='#fff' size="xl" />
            </div>
        </div>
    }

    return (
        <div data-testid={resolvedTestId} data-record-id={transaction?._id}>

            <div className="fv_pay_now_checkout_wrapper" data-testid={buildAutomationChildTestId(resolvedTestId, "checkout")}>
                {clientSecret && stripePromise && (
                    <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{ clientSecret, onComplete: onCheckoutComplete }}
                    >
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                )}
            </div>

            <div className="fv_pay_now_footer" data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                <Button
                    data-testid={buildAutomationChildTestId(resolvedTestId, "back")}
                    className='form-content-btn-outline mt-5'
                    disabled={loading}
                    onClick={() => {
                        onBackClick();
                    }}
                >
                    {loading ? <ThreeBounce color="#000" /> : t("Back")}
                </Button>
            </div>
        </div>
    )
}

export default EmbeddedCheckoutUI;
